/**
 * Pending Elicitation store (Zustand).
 *
 * Holds the single active MCP elicitation prompt waiting for user input.
 * The SSE handler (`useSessionEvents`) calls `open` when an
 * `elicitation_prompt` event arrives; `ElicitationDialog` calls `close`
 * on submit / dismiss.
 *
 * Design: only one elicitation can be pending at a time per MCP spec
 * (the tool handler blocks until it receives one answer, and llm-service
 * processes turns serially). The store therefore holds a single
 * `PendingElicitation | null` rather than a map.
 *
 * The store does NOT have a defensive timeout — unlike UIActions (where
 * the backend dispatches asynchronously), an elicitation blocks the LLM
 * stream until resolved. If the stream drops, the SSE connection reconnects
 * and the stream resumes from where it left off (Last-Event-ID replay);
 * if the tool context is cancelled on the server, the user's next POST
 * will return 409 FailedPrecondition, which the dialog handles gracefully.
 */

import { create } from 'zustand';

import { createLogger } from '@/utils/logger';

const logger = createLogger('pendingElicitations');

export interface PendingElicitation {
  /** Server-generated UUID correlating prompt ↔ ResolveElicitation call. */
  elicitationId: string;
  /** Human-readable message shown as the dialog title / description. */
  message: string;
  /**
   * JSON Schema (flat per MCP 2025-06-18 spec) — string-encoded.
   * `ElicitationDialog` parses this to render the form fields.
   */
  requestedSchemaJson: string;
  /** Session ID needed to build the POST URL. */
  sessionId: string;
}

interface PendingElicitationsState {
  /** Active elicitation, or null when none pending. */
  pending: PendingElicitation | null;
  /** Store an incoming elicitation prompt. Replaces any existing (shouldn't happen in practice). */
  open: (elicitation: PendingElicitation) => void;
  /** Clear the pending elicitation (after submit / dismiss / error). */
  close: () => void;
}

export const usePendingElicitationsStore = create<PendingElicitationsState>((set, get) => ({
  pending: null,
  open: (elicitation) => {
    // The MCP spec requires serial elicitation per stream, so two
    // pending entries at once shouldn't happen — but if it does
    // (server bug, retry, replay), warn so a future debugging
    // session has a breadcrumb instead of a silent state replacement.
    const existing = get().pending;
    if (existing && existing.elicitationId !== elicitation.elicitationId) {
      logger.warn('elicitation_replaced_while_pending', {
        previousId: existing.elicitationId,
        newId: elicitation.elicitationId,
      });
    }
    set({ pending: elicitation });
  },
  close: () => set({ pending: null }),
}));

// -- Exported for tests only --
/** @internal reset the store to its initial state. */
export function __resetPendingElicitationsForTests(): void {
  usePendingElicitationsStore.setState({ pending: null });
}
